import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useAuth } from 'shared/providers/AuthProvider';
import { env } from 'env';

const churnZeroAppKey = env.REACT_APP_CHURN_ZERO_APP_KEY || '';

const isProduction = env.REACT_APP_PRODUCTION === 'true';

type PushArgsType = Array<string | number | { [key: string]: string } | boolean>;

interface ChurnZero {
  push: (args: PushArgsType) => void;
  debug: () => void;
  verify: () => void;
}

enum ChurnZeroActions {
  setApiKey = 'setAppKey',
  setAttribute = 'setAttribute',
  setContact = 'setContact',
  incrementAttribute = 'incrementAttribute',
  setModule = 'setModule',
  silent = 'silent',
  trackEvent = 'trackEvent',
  open = 'open',
  close = 'close',
  stop = 'stop',
}

declare global {
  interface Window {
    ChurnZero?: ChurnZero;
  }
}

// ChurnZero is a third party service that we use to track user engagement
// and retention. This hook is used to push events to ChurnZero.
// for documentation see
//https://support.churnzero.com/hc/en-us/articles/360004683552-Integrate-ChurnZero-using-Javascript

// it is possible to run push commands straight on the console

interface IdentifyUserProps {
  email: string;
  firstName: string;
  lastName: string;
  clinicCrmId: string | undefined;
}

const useChurnZero = (): {
  identifyUser: (props: IdentifyUserProps) => void;
  setContactAttribute: (key: string, value: string) => void;
  setAccountAttribute: (key: string, value: string) => void;
  trackEvent: (
    eventName: string,
    description?: string,
    quantity?: number,
    customFields?: {
      [key: string]: string;
    },
  ) => void;
  verify: () => void;
  incrementAccountAttribute: (key: string, value: string) => void;
  incrementContactAttribute: (key: string, value: string) => void;
  stop: () => void;
  setModule: (module: string) => void;
  silentMode: (state: boolean) => void;
  openSuccessPanel: () => void;
  closeSuccessPanel: () => void;
} => {
  const { isAuthenticated } = useAuth();
  const isAuthenticatedRef = useRef(isAuthenticated);
  useEffect(() => {
    isAuthenticatedRef.current = isAuthenticated;
  }, [isAuthenticated]);

  useEffect(() => {
    if (isProduction) {
      try {
        if (window.ChurnZero) {
          // ----- Uncomment this to enable ChurnZero debug mode -----
          // window.ChurnZero.debug();
          // ---------------------------------------------------------

          window.ChurnZero.push([ChurnZeroActions.setApiKey, churnZeroAppKey]);
        } else {
          console.error('Unable to initialize ChurnZero');
        }
      } catch (error) {
        console.log(error);
      }
    }
  }, []);

  // Attributes about the account or features can be tracked in ChurnZero by using the
  // setAttribute method. Attributes can be set one at a time or in batch via a document.
  const setAccountAttribute = useCallback((key: string, value: string) => {
    try {
      if (!isProduction) return;
      if (window.ChurnZero && isAuthenticatedRef.current) {
        window.ChurnZero.push([ChurnZeroActions.setAttribute, 'account', key, value]);
      } else {
        console.error('Unable to set account attribute for ChurnZero.', { key, value });
      }
    } catch (error) {
      console.log(error);
    }
  }, []);

  const setContactAttribute = useCallback((key: string, value: string) => {
    try {
      if (!isProduction) return;
      if (window.ChurnZero && isAuthenticatedRef.current) {
        window.ChurnZero.push([ChurnZeroActions.setAttribute, 'contact', key, value]);
      } else {
        console.error('Unable to set contact attribute for ChurnZero.', { key, value });
      }
    } catch (error) {
      console.log(error);
    }
  }, []);

  const identifyUser = useCallback(
    ({ email, firstName, lastName, clinicCrmId }: IdentifyUserProps) => {
      try {
        if (!isProduction) return;
        if (window.ChurnZero && isAuthenticatedRef.current && email && clinicCrmId) {
          window.ChurnZero.push([ChurnZeroActions.setContact, clinicCrmId, email]);
          setContactAttribute('FirstName', firstName);
          setContactAttribute('LastName', lastName);
        } else {
          console.error('Unable to identify account/user for ChurnZero.');
        }
      } catch (error) {
        console.log(error);
      }
    },
    [setContactAttribute],
  );

  // Events are a key element of ChurnZero as it allows you to track what a user is doing.
  // For events to be tracked the setCustomer, etc code must have executed.
  // To track an event add the following code wherever you want to track an event:
  const trackEvent = useCallback(
    (eventName: string, description?: string, quantity?: number, customFields?: { [key: string]: string }) => {
      try {
        if (!isProduction) return;
        const churnZeroArray: PushArgsType = [ChurnZeroActions.trackEvent];
        if (eventName) {
          churnZeroArray.push(eventName);
        }
        if (description) {
          churnZeroArray.push(description);
        }
        if (quantity) {
          churnZeroArray.push(quantity);
        }
        if (customFields) {
          churnZeroArray.push(customFields);
        }

        if (window.ChurnZero && isAuthenticatedRef.current) {
          window.ChurnZero.push(churnZeroArray);
        } else {
          console.error('Unable to track event for ChurnZero.');
        }
      } catch (error) {
        console.log(error);
      }
    },
    [],
  );

  // Once the appKey and setContact calls have been made your app is successfully connected!
  // You can verify the details of your connection by opening the developer tools and
  // calling verify from the ChurnZero object.
  // This will provide you with the current details about the connected app, account,
  // contact, and connection status. (In the console)
  const verify = useCallback(() => {
    try {
      if (!isProduction) return;

      if (window.ChurnZero && isAuthenticatedRef.current) {
        window.ChurnZero.verify();
      } else {
        console.error('Unable to track event for ChurnZero.');
      }
    } catch (error) {
      console.log(error);
    }
  }, []);

  // Numeric attributes can be incremented via the incrementAttriubute Method.
  // Like the setAttribute method, numeric values can be incremented one at a time or
  // batch via a document.
  const incrementAccountAttribute = useCallback((key: string, value: string) => {
    try {
      if (!isProduction) return;
      if (window.ChurnZero && isAuthenticatedRef.current) {
        window.ChurnZero.push([ChurnZeroActions.incrementAttribute, 'account', key, value]);
      } else {
        console.error('Unable to increment account attribute for ChurnZero.', { key, value });
      }
    } catch (error) {
      console.log(error);
    }
  }, []);

  const incrementContactAttribute = useCallback((key: string, value: string) => {
    try {
      if (!isProduction) return;
      if (window.ChurnZero && isAuthenticatedRef.current) {
        window.ChurnZero.push([ChurnZeroActions.incrementAttribute, 'contact', key, value]);
      } else {
        console.error('Unable to increment account attribute for ChurnZero.', { key, value });
      }
    } catch (error) {
      console.log(error);
    }
  }, []);

  // By default the URL and the title of the page are transmitted with the event data and
  // the time in module data but if you would prefer not to include this data simply
  // turn off URL tracking at any time. If you want to be sure no URL data is ever sent be
  // sure to turn it off prior to setting the contact identity.
  const stop = useCallback(() => {
    try {
      if (!isProduction) return;
      if (window.ChurnZero && isAuthenticatedRef.current) {
        window.ChurnZero.push([ChurnZeroActions.stop]);
      } else {
        console.error('Unable to stop ChurnZero.');
      }
    } catch (error) {
      console.log(error);
    }
  }, []);

  // Time in app is recorded to a standard "Default" module if no other module is set
  // but if you would like to track time in app at a more granular level you can
  // specify which module is currently active. The last set module will be active until either the page reloads or a new module is set.
  const setModule = useCallback((moduleName: string) => {
    try {
      if (!isProduction) return;
      if (window.ChurnZero && isAuthenticatedRef.current) {
        window.ChurnZero.push([ChurnZeroActions.setModule, moduleName]);
      } else {
        console.error('Unable to set module for ChurnZero.', { moduleName });
      }
    } catch (error) {
      console.log(error);
    }
  }, []);

  // If a situation arises where you would like to hide all visible aspects of the Success Panel including any currently displayed or future announcements but you still want usage tracking you can activate Silent Mode.
  const silentMode = useCallback((state: boolean) => {
    try {
      if (!isProduction) return;
      if (window.ChurnZero && isAuthenticatedRef.current) {
        window.ChurnZero.push([ChurnZeroActions.silent, state]);
      } else {
        console.error('Unable to set silent mode for ChurnZero.');
      }
    } catch (error) {
      console.log(error);
    }
  }, []);

  // If you would like to open the success panel via an action other than clicking the
  // launch avatar this can be done using a single javascript command:
  const openSuccessPanel = useCallback(() => {
    try {
      if (!isProduction) return;
      if (window.ChurnZero && isAuthenticatedRef.current) {
        window.ChurnZero.push([ChurnZeroActions.open]);
      } else {
        console.error('Unable to open success panel for ChurnZero.');
      }
    } catch (error) {
      console.log(error);
    }
  }, []);

  // If you would like to close the panel via an action other than clicking the close
  // button within the panel this can be done using a single javascript command:
  const closeSuccessPanel = useCallback(() => {
    try {
      if (!isProduction) return;
      if (window.ChurnZero && isAuthenticatedRef.current) {
        window.ChurnZero.push([ChurnZeroActions.close]);
      } else {
        console.error('Unable to close success panel for ChurnZero.');
      }
    } catch (error) {
      console.log(error);
    }
  }, []);

  const returnValue = useMemo(() => {
    return {
      identifyUser,
      setAccountAttribute,
      setContactAttribute,
      trackEvent,
      verify,
      incrementAccountAttribute,
      incrementContactAttribute,
      stop,
      setModule,
      silentMode,
      openSuccessPanel,
      closeSuccessPanel,
    };
  }, [
    closeSuccessPanel,
    identifyUser,
    incrementAccountAttribute,
    incrementContactAttribute,
    openSuccessPanel,
    setAccountAttribute,
    setContactAttribute,
    setModule,
    silentMode,
    stop,
    trackEvent,
    verify,
  ]);

  return returnValue;
};

export default useChurnZero;
